import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { ArrowLeft, Shield, Upload, Trash2, Edit, UserPlus, Settings, MessageSquare, Calendar, Star, RefreshCw, Filter, ChevronDown, ChevronUp } from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_CONFIG = {
  uploads: { label: "Uploads", icon: Upload, color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/30" },
  sletninger: { label: "Sletninger", icon: Trash2, color: "text-red-400", bg: "bg-red-500/10 border-red-500/30" },
  indhold_oprettet: { label: "Nyt indhold", icon: Calendar, color: "text-green-400", bg: "bg-green-500/10 border-green-500/30" },
  indhold_redigeret: { label: "Redigeret indhold", icon: Edit, color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/30" },
  profil: { label: "Profil ændringer", icon: Edit, color: "text-purple-400", bg: "bg-purple-500/10 border-purple-500/30" },
  roller: { label: "Rolle ændringer", icon: Shield, color: "text-orange-400", bg: "bg-orange-500/10 border-orange-500/30" },
  adgang: { label: "Adgang & koder", icon: UserPlus, color: "text-cyan-400", bg: "bg-cyan-500/10 border-cyan-500/30" },
  bookings: { label: "Bookings", icon: Calendar, color: "text-indigo-400", bg: "bg-indigo-500/10 border-indigo-500/30" },
  beskeder: { label: "Beskeder", icon: MessageSquare, color: "text-pink-400", bg: "bg-pink-500/10 border-pink-500/30" },
  indstillinger: { label: "Indstillinger", icon: Settings, color: "text-gray-400", bg: "bg-gray-500/10 border-gray-500/30" },
  andet: { label: "Andet", icon: Star, color: "text-gray-500", bg: "bg-gray-500/10 border-gray-500/30" },
};

const AuditLogPage = () => {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [filterCategory, setFilterCategory] = useState(null);
  const [filterUser, setFilterUser] = useState(null);
  const [expandedEntry, setExpandedEntry] = useState(null);

  const headers = { Authorization: `Bearer ${getToken()}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days, limit: 500 };
      if (filterCategory) params.category = filterCategory;
      const [logRes, summaryRes] = await Promise.all([
        axios.get(`${API}/audit-log`, { headers, params }),
        axios.get(`${API}/audit-log/summary`, { headers, params: { days } }),
      ]);
      setEntries(logRes.data.entries);
      setSummary(summaryRes.data);
    } catch {
      // Access denied or error
    } finally {
      setLoading(false);
    }
  }, [days, filterCategory]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredEntries = filterUser
    ? entries.filter(e => e.user_name === filterUser)
    : entries;

  const formatTime = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return "Lige nu";
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min siden`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} timer siden`;
    return d.toLocaleDateString("da-DK", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="min-h-screen bg-[#0d0d0f] text-white p-4 md:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate("/medlemmer")} className="p-2 hover:bg-white/5 rounded-lg transition-colors" data-testid="audit-back-btn">
          <ArrowLeft size={20} />
        </button>
        <div className="flex items-center gap-2">
          <Shield className="text-red-500" size={22} />
          <h1 className="text-xl font-['Oswald'] font-bold tracking-wide">AKTIVITETSLOG</h1>
        </div>
        <button onClick={fetchData} disabled={loading} className="ml-auto p-2 hover:bg-white/5 rounded-lg transition-colors" data-testid="audit-refresh-btn">
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Time filter */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {[1, 3, 7, 14, 30, 90].map(d => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`px-3 py-1.5 rounded-lg text-xs font-['Oswald'] uppercase tracking-wider transition-all ${
              days === d ? "bg-red-500/20 text-red-400 border border-red-500/40" : "bg-white/5 text-gray-400 border border-white/10 hover:border-white/20"
            }`}
            data-testid={`audit-days-${d}`}
          >
            {d === 1 ? "I dag" : `${d} dage`}
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-6">
          {Object.entries(summary.categories || {}).map(([cat, count]) => {
            const cfg = CATEGORY_CONFIG[cat] || CATEGORY_CONFIG.andet;
            const Icon = cfg.icon;
            const isActive = filterCategory === cat;
            return (
              <button
                key={cat}
                onClick={() => setFilterCategory(isActive ? null : cat)}
                className={`flex items-center gap-2.5 p-3 rounded-xl border transition-all text-left ${
                  isActive ? `${cfg.bg} ring-1 ring-white/20` : "bg-white/[0.02] border-white/5 hover:border-white/10"
                }`}
                data-testid={`audit-filter-${cat}`}
              >
                <Icon size={16} className={cfg.color} />
                <div>
                  <div className={`text-lg font-bold ${cfg.color}`}>{count}</div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider">{cfg.label}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Active Users */}
      {summary?.users?.length > 0 && (
        <div className="mb-6 p-4 rounded-xl bg-white/[0.02] border border-white/5">
          <h3 className="text-xs font-['Oswald'] uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-1.5">
            <Filter size={12} /> Aktive brugere
          </h3>
          <div className="flex gap-2 flex-wrap">
            {summary.users.map(u => (
              <button
                key={u.name}
                onClick={() => setFilterUser(filterUser === u.name ? null : u.name)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-all ${
                  filterUser === u.name ? "bg-red-500/20 text-red-300 border border-red-500/30" : "bg-white/5 text-gray-400 border border-white/10 hover:border-white/20"
                }`}
                data-testid={`audit-user-${u.name}`}
              >
                <span className="font-medium">{u.name}</span>
                <span className="text-gray-600">({u.actions})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Activity Feed */}
      <div className="space-y-1.5">
        {loading && entries.length === 0 ? (
          <div className="text-center py-16 text-gray-600">
            <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
            <p className="text-sm">Indlæser...</p>
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="text-center py-16 text-gray-600">
            <Shield size={32} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">Ingen aktivitet fundet</p>
          </div>
        ) : (
          filteredEntries.map((entry, idx) => {
            const cfg = CATEGORY_CONFIG[entry.category] || CATEGORY_CONFIG.andet;
            const Icon = cfg.icon;
            const isExpanded = expandedEntry === idx;

            return (
              <div
                key={idx}
                className={`flex items-start gap-3 p-3 rounded-lg border transition-all cursor-pointer ${
                  entry.success ? "bg-white/[0.02] border-white/5 hover:border-white/10" : "bg-red-500/5 border-red-500/20"
                }`}
                onClick={() => setExpandedEntry(isExpanded ? null : idx)}
                data-testid={`audit-entry-${idx}`}
              >
                <div className={`p-1.5 rounded-lg mt-0.5 ${cfg.bg} border`}>
                  <Icon size={13} className={cfg.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-white font-medium">{entry.description}</span>
                    {!entry.success && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">FEJL</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-500">
                    <span className="font-medium text-gray-400">{entry.user_name}</span>
                    <span>·</span>
                    <span>{formatTime(entry.timestamp)}</span>
                  </div>
                  {isExpanded && (
                    <div className="mt-2 p-2 rounded bg-black/30 text-[11px] text-gray-500 font-mono">
                      <div><span className="text-gray-600">Metode:</span> <span className={entry.method === "DELETE" ? "text-red-400" : "text-green-400"}>{entry.method}</span></div>
                      <div><span className="text-gray-600">Sti:</span> {entry.path}</div>
                      <div><span className="text-gray-600">Status:</span> <span className={entry.success ? "text-green-400" : "text-red-400"}>{entry.status_code}</span></div>
                      <div><span className="text-gray-600">Tidspunkt:</span> {new Date(entry.timestamp).toLocaleString("da-DK")}</div>
                      <div><span className="text-gray-600">Rolle:</span> {entry.user_role}</div>
                    </div>
                  )}
                </div>
                <div className="text-gray-600 flex-shrink-0">
                  {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AuditLogPage;
